import { useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2 } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { imgUrl, usd } from "@/lib/api";
import { useLang } from "@/i18n";

export default function CartDrawer() {
  const { items, open, setOpen, setQty, remove, total } = useCart();
  const navigate = useNavigate();
  const { t } = useLang();

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        data-testid="cart-drawer"
        className="bg-[#0B1120] text-slate-100 border-l border-white/10 w-full sm:max-w-md flex flex-col"
      >
        <SheetHeader>
          <SheetTitle className="font-display text-2xl text-white">
            {t("cart_title")}
            <span className="text-[#ED1C24]">.</span>
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto mt-6 space-y-4 pr-1">
          {items.length === 0 && (
            <p data-testid="cart-empty-message" className="text-slate-400 text-sm pt-10 text-center">
              {t("cart_empty")}
            </p>
          )}
          {items.map((item) => (
            <div
              key={item.id}
              data-testid={`cart-item-${item.id}`}
              className="flex gap-4 border border-white/10 bg-white/[0.03] p-3"
            >
              <img
                src={imgUrl(item.image)}
                alt={item.name}
                className="w-20 h-20 object-cover"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{item.name}</p>
                <p className="text-[#FFC61A] text-sm mt-1">{usd(item.price)}</p>
                <div className="flex items-center gap-2 mt-2">
                  <button
                    data-testid={`cart-qty-minus-${item.id}`}
                    onClick={() => setQty(item.id, item.qty - 1)}
                    className="w-7 h-7 border border-white/15 flex items-center justify-center hover:border-[#ED1C24] transition-colors"
                    aria-label="Decrease quantity"
                  >
                    <Minus size={12} />
                  </button>
                  <span className="text-sm w-6 text-center">{item.qty}</span>
                  <button
                    data-testid={`cart-qty-plus-${item.id}`}
                    onClick={() => setQty(item.id, item.qty + 1)}
                    className="w-7 h-7 border border-white/15 flex items-center justify-center hover:border-[#ED1C24] transition-colors"
                    aria-label="Increase quantity"
                  >
                    <Plus size={12} />
                  </button>
                  <button
                    data-testid={`cart-remove-${item.id}`}
                    onClick={() => remove(item.id)}
                    className="ml-auto text-slate-500 hover:text-[#ED1C24] transition-colors"
                    aria-label="Remove item"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {items.length > 0 && (
          <div className="border-t border-white/10 pt-5 mt-4 space-y-4">
            <div className="flex justify-between items-baseline">
              <span className="text-sm text-slate-400">{t("cart_subtotal")}</span>
              <span data-testid="cart-subtotal" className="font-display text-2xl text-white">
                {usd(total)}
              </span>
            </div>
            <button
              data-testid="go-to-checkout-btn"
              onClick={() => {
                setOpen(false);
                navigate("/checkout");
              }}
              className="w-full bg-[#ED1C24] hover:bg-[#C81016] text-white font-medium py-3.5 transition-colors duration-300 hover:shadow-[0_0_30px_rgba(237,28,36,0.4)]"
            >
              {t("cart_checkout")}
            </button>
            <button
              data-testid="continue-shopping-btn"
              onClick={() => {
                setOpen(false);
                navigate("/shop");
              }}
              className="w-full border border-white/15 hover:border-[#ED1C24] text-slate-300 hover:text-white py-3 text-sm transition-colors duration-300"
            >
              {t("cart_continue")}
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
